import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { PurchaseOrder, PORequirement, Me } from "../../api/types";
import { S } from "../../styles";
import { hasRole } from "../../auth/roleHelpers";
import { POStatusBadge } from "../../components/POStatusBadge";
import {
  ReceivedCount,
  TraceTermsPanel,
  UnitIdentity,
} from "../../components/OrderLine";
import { ShortId } from "../../components/ShortId";
import { SimulateInboundControls } from "../simulation/SimulateInboundControls";
import { AuditTrail } from "../audit/AuditTrail";
import {
  LoadingState,
  ErrorState,
  EmptyState,
} from "../../components/QueryStates";

const REQ_STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  fulfilled: { bg: "#e9f9ef", fg: "#1c7a3e" },
  open: { bg: "#fffaf0", fg: "#8a5a1c" },
  cancelled: { bg: "#f1f3f5", fg: "#666666" },
};

/**
 * What this order was bought for.
 *
 * There is no PO→requirement column and there should not be one — a
 * consolidated RFQ covers several requirements and one order against it
 * satisfies all of them — so the trace runs through the RFQ. Until this
 * existed, answering "did this order close its demand?" meant reading a
 * `FULFILLED_BY_RECEIPT` audit event's JSON or querying the database.
 */
export function CoverageList({
  requirements,
  loading = false,
}: {
  requirements?: PORequirement[] | null;
  loading?: boolean;
}) {
  if (loading) return <p style={S.muted}>Loading requirements…</p>;
  // Null is "not loaded", which is not the same claim as "covers nothing" —
  // only the detail route populates this. Say nothing rather than something
  // false.
  if (requirements == null) return null;
  if (requirements.length === 0)
    return (
      <p style={S.muted}>
        No requirement is linked to this order — its RFQ predates demand
        coverage.
      </p>
    );

  return (
    <>
      {requirements.map((r) => {
        const tone = REQ_STATUS_TONE[r.status] ?? REQ_STATUS_TONE.open;
        const short =
          r.quantity_covered != null &&
          r.quantity_required != null &&
          r.quantity_covered < r.quantity_required;
        return (
          <div
            key={r.requirement_id}
            style={{
              background: "#f8fafb",
              borderRadius: 6,
              padding: "8px 12px",
              marginBottom: 6,
              fontSize: 12,
            }}
          >
            <span
              style={{
                background: tone.bg,
                color: tone.fg,
                borderRadius: 10,
                padding: "1px 8px",
                fontWeight: 600,
              }}
            >
              {r.status}
            </span>
            <strong style={{ marginLeft: 8 }}>{r.part_number ?? "—"}</strong>
            {r.condition_code && (
              <span style={{ ...S.muted, marginLeft: 6 }}>
                ({r.condition_code})
              </span>
            )}
            {/* The requirement's only identity, and the whole point of this
                list: several open demands for the same part are otherwise
                indistinguishable, so naming the part alone cannot say which
                one this order closed. Matches the REQ chip on Requirements. */}
            <span style={{ marginLeft: 8 }}>
              <ShortId id={r.requirement_id} prefix="REQ" />
            </span>
            {r.quantity_covered != null && (
              <span style={{ marginLeft: 8 }}>
                {/* The link's quantity, not the requirement's: it is what
                    close_fulfilled_by_receipt sums, so it is the number that
                    explains why this closed or did not. */}
                {short
                  ? `${r.quantity_covered} of ${r.quantity_required} covered`
                  : `qty ${r.quantity_covered}`}
              </span>
            )}
            {r.is_aog && (
              <span
                style={{
                  marginLeft: 8,
                  color: "#c92a2a",
                  fontWeight: 700,
                }}
              >
                AOG
              </span>
            )}
            {r.work_order_ref && (
              <span style={{ ...S.muted, marginLeft: 8 }}>
                {r.work_order_ref}
              </span>
            )}
            {r.required_by_date && (
              <span style={{ ...S.muted, marginLeft: 8 }}>
                due {r.required_by_date}
              </span>
            )}
          </div>
        );
      })}
    </>
  );
}

export function PurchaseOrdersPanel({ me }: { me: Me | null }) {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const canManagerApprove = hasRole(me, "manager");
  const canRedispatch = hasRole(me, "buyer", "planner");
  const canViewAudit = hasRole(me, "finance", "manager");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["purchase-orders"],
    queryFn: () => api.listPurchaseOrders(0, 50),
    refetchInterval: 8_000,
  });
  const pos = data?.items ?? [];
  const total = data?.total ?? 0;

  const { data: shipmentData, isLoading: shipmentsLoading } = useQuery({
    queryKey: ["po-shipments", expandedId],
    queryFn: () => api.getPOShipments(expandedId!),
    enabled: !!expandedId,
    staleTime: 10_000,
  });
  const expandedShipments = shipmentData ?? [];

  // The demand this order was placed to satisfy. Only the detail route carries
  // it — the list would need a join per row — so it is fetched on expand.
  const { data: expandedDetail, isLoading: detailLoading } = useQuery({
    queryKey: ["po-detail", expandedId],
    queryFn: () => api.getPurchaseOrder(expandedId!),
    enabled: !!expandedId,
    staleTime: 10_000,
  });

  const invalidatePOs = () =>
    void queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });

  const approvePOMutation = useMutation({
    mutationFn: ({ poId, note }: { poId: string; note?: string }) =>
      api.managerApprovePO(poId, note),
    onSuccess: () => {
      setActionErr(null);
      invalidatePOs();
    },
    onError: (e) => setActionErr(e.message),
    onSettled: () => setActionBusyId(null),
  });

  const rejectPOMutation = useMutation({
    mutationFn: ({ poId, note }: { poId: string; note?: string }) =>
      api.managerRejectPO(poId, note),
    onSuccess: () => {
      setActionErr(null);
      invalidatePOs();
    },
    onError: (e) => setActionErr(e.message),
    onSettled: () => setActionBusyId(null),
  });

  const redispatchMutation = useMutation({
    mutationFn: (poId: string) => api.redispatchPO(poId),
    onSuccess: () => {
      setActionErr(null);
      invalidatePOs();
    },
    onError: (e) => setActionErr(e.message),
    onSettled: () => setActionBusyId(null),
  });

  const pending = pos.filter((p) => p.status === "PendingApproval");
  const others = pos.filter((p) => p.status !== "PendingApproval");

  if (isLoading) return <LoadingState label="Loading purchase orders…" />;
  if (error) return <ErrorState error={error} />;

  const renderPO = (po: PurchaseOrder) => {
    const isExpanded = expandedId === po.id;
    const isBusy = actionBusyId === po.id;

    return (
      <div key={po.id} style={S.card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <span style={{ fontWeight: 700, fontSize: 15 }}>
              {po.po_number}
            </span>
            <span style={{ marginLeft: 10 }}>
              <POStatusBadge s={po.status} />
            </span>
            {po.dispatch_channel && (
              <span style={{ ...S.muted, marginLeft: 8, fontSize: 11 }}>
                via {po.dispatch_channel}
              </span>
            )}
            {po.supplier_po_ref && (
              <span style={{ ...S.muted, marginLeft: 8, fontSize: 11 }}>
                Supplier: {po.supplier_po_ref}
              </span>
            )}
            {po.erp_po_ref && (
              <span style={{ ...S.muted, marginLeft: 8, fontSize: 11 }}>
                ERP: {po.erp_po_ref}
              </span>
            )}
          </div>
          <button
            style={S.btnSm}
            onClick={() => setExpandedId(isExpanded ? null : po.id)}
          >
            {isExpanded ? "▲ Collapse" : "▼ Details"}
          </button>
        </div>

        {/*
          The line first, the total second. The total is derived from the unit
          price, and a buyer checking an order against an invoice or a partial
          shipment needs the terms it was derived from.
        */}
        {po.part_number && (
          <div style={{ marginTop: 6, fontSize: 13 }}>
            <strong>{po.part_number}</strong>
            {po.condition_code && (
              <span style={{ ...S.muted, marginLeft: 6 }}>
                ({po.condition_code})
              </span>
            )}
            {po.quantity != null && po.unit_price != null && (
              <span style={{ marginLeft: 8 }}>
                {po.quantity} {po.unit_of_measure ?? "EA"} × {po.currency}{" "}
                {parseFloat(po.unit_price).toFixed(2)}
              </span>
            )}
          </div>
        )}

        <div
          style={{
            ...S.muted,
            marginTop: 6,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "4px 16px",
          }}
        >
          <span>
            Amount:{" "}
            <strong>
              {po.currency}{" "}
              {parseFloat(po.total_amount).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </strong>
          </span>
          <span>Created: {new Date(po.created_at).toLocaleString()}</span>
          {po.sent_at && (
            <span>Sent: {new Date(po.sent_at).toLocaleString()}</span>
          )}
        </div>

        <TraceTermsPanel commitment={po.trace_commitment} />

        {po.status === "PendingApproval" && canManagerApprove && (
          <div
            style={{
              marginTop: 12,
              borderTop: "1px solid #e8eaf0",
              paddingTop: 12,
            }}
          >
            <p style={{ ...S.muted, marginBottom: 8, fontSize: 13 }}>
              ⚠ This PO exceeds the auto-approve threshold and requires manager
              approval.
            </p>
            <input
              style={{ ...S.input, marginBottom: 8 }}
              placeholder="Approval notes (optional)…"
              value={notes[po.id] ?? ""}
              onChange={(e) =>
                setNotes((prev) => ({ ...prev, [po.id]: e.target.value }))
              }
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                style={{ ...S.btn, background: "#2f9e44", padding: "7px 16px" }}
                onClick={() => {
                  setActionBusyId(po.id);
                  approvePOMutation.mutate({
                    poId: po.id,
                    note: notes[po.id],
                  });
                }}
                disabled={isBusy}
              >
                ✓ Approve PO
              </button>
              <button
                style={{ ...S.btn, background: "#c92a2a", padding: "7px 16px" }}
                onClick={() => {
                  setActionBusyId(po.id);
                  rejectPOMutation.mutate({
                    poId: po.id,
                    note: notes[po.id],
                  });
                }}
                disabled={isBusy}
              >
                ✗ Reject PO
              </button>
            </div>
          </div>
        )}

        {/*
          What the supplier answered. A rejection and a short confirmation are
          both things a buyer has to act on, and neither is visible from the PO
          status alone — an order acknowledged at half the quantity still reads
          as "Acknowledged".
        */}
        {po.supplier_ack && (
          <div
            style={{
              marginTop: 10,
              padding: "6px 10px",
              borderRadius: 4,
              fontSize: 12,
              background: po.supplier_ack.accepted ? "#ebfbee" : "#fff5f5",
              border: `1px solid ${po.supplier_ack.accepted ? "#b2f2bb" : "#ffc9c9"}`,
              color: po.supplier_ack.accepted ? "#2b6a2f" : "#a61e1e",
            }}
          >
            <strong>
              {po.supplier_ack.accepted
                ? "Supplier accepted"
                : "Supplier did not accept"}
            </strong>{" "}
            ({po.supplier_ack.type}
            {po.supplier_ack.transaction
              ? ` · ${po.supplier_ack.transaction}`
              : ""}
            )
            {po.supplier_ack.notes && (
              <div style={{ marginTop: 4, whiteSpace: "pre-line" }}>
                {po.supplier_ack.notes}
              </div>
            )}
            {(po.supplier_ack.discrepancies ?? []).length > 0 && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {(po.supplier_ack.discrepancies ?? []).map((d) => (
                  <li key={d.field}>
                    {d.field}: ordered {String(d.ordered)}, confirmed{" "}
                    {String(d.acknowledged)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {(po.status === "Draft" || po.status === "Approved") &&
          canRedispatch && (
            <div style={{ marginTop: 10 }}>
              {/*
                Why the order did not go out, next to the button that retries
                it. An undelivered order used to be shown as Sent, so there was
                nothing here to explain and nothing to act on.
              */}
              {po.dispatch_error && (
                <div
                  style={{
                    marginBottom: 8,
                    padding: "6px 10px",
                    borderRadius: 4,
                    background: "#fff4e6",
                    border: "1px solid #ffd8a8",
                    fontSize: 12,
                    color: "#8c4a00",
                  }}
                >
                  <strong>Not sent</strong>
                  {po.dispatch_channel ? ` (${po.dispatch_channel})` : ""}:{" "}
                  {po.dispatch_error}
                </div>
              )}
              <button
                style={{ ...S.btnSm, background: "#e67700" }}
                onClick={() => {
                  setActionBusyId(po.id + "-redispatch");
                  redispatchMutation.mutate(po.id);
                }}
                disabled={actionBusyId === po.id + "-redispatch"}
              >
                ↺ Re-dispatch
              </button>
              <span style={{ ...S.muted, marginLeft: 8, fontSize: 12 }}>
                Replay failed dispatch (Kafka replay)
              </span>
            </div>
          )}

        {isExpanded && (
          <div
            style={{
              marginTop: 12,
              borderTop: "1px solid #f0f0f0",
              paddingTop: 12,
            }}
          >
            <div style={{ ...S.muted, fontSize: 12, marginBottom: 8 }}>
              ID: <code>{po.id}</code>
            </div>

            {/*
              Unit identity is shown on every order, including when it is
              absent — that is the state receiving has to resolve, and an
              omitted row would leave it looking like nothing was required.
            */}
            <div style={{ ...S.muted, fontSize: 12, marginBottom: 8 }}>
              {po.batch_number ? "Lot / batch" : "Serial numbers"}:{" "}
              <UnitIdentity
                serialNumbers={po.serial_numbers}
                batchNumber={po.batch_number}
                quantity={po.quantity}
              />
            </div>

            {po.notes && <p style={{ ...S.muted, fontSize: 12 }}>{po.notes}</p>}

            <h3 style={{ ...S.h3, marginBottom: 8, fontSize: 14 }}>
              Satisfies
              {!detailLoading &&
                expandedDetail?.requirements != null &&
                ` (${expandedDetail.requirements.length})`}
            </h3>
            <CoverageList
              requirements={expandedDetail?.requirements}
              loading={detailLoading}
            />

            <h3 style={{ ...S.h3, marginBottom: 8, fontSize: 14 }}>
              Shipments
              {shipmentsLoading
                ? " (loading…)"
                : ` (${expandedShipments.length})`}
            </h3>
            {!shipmentsLoading && expandedShipments.length === 0 ? (
              <p style={S.muted}>No shipments yet.</p>
            ) : (
              expandedShipments.map((ship) => (
                <div
                  key={ship.id}
                  style={{
                    background: "#f8fafb",
                    borderRadius: 6,
                    padding: "8px 12px",
                    marginBottom: 6,
                    fontSize: 12,
                  }}
                >
                  <POStatusBadge s={ship.status} />
                  {ship.tracking_number && (
                    <span style={{ marginLeft: 8 }}>
                      {ship.carrier}: <strong>{ship.tracking_number}</strong>
                    </span>
                  )}
                  {ship.estimated_delivery && (
                    <span style={{ ...S.muted, marginLeft: 8 }}>
                      ETA: {ship.estimated_delivery}
                    </span>
                  )}
                  {ship.received_at && (
                    <span style={{ ...S.muted, marginLeft: 8 }}>
                      Received:{" "}
                      {new Date(ship.received_at).toLocaleDateString()}
                    </span>
                  )}
                  <span style={{ marginLeft: 8 }}>
                    <ReceivedCount
                      received={ship.quantity_received}
                      ordered={po.quantity}
                    />
                  </span>
                </div>
              ))
            )}

            <SimulateInboundControls
              po={po}
              shipment={expandedShipments[0]}
              onSimulated={() => {
                // Deliberately stays expanded: a short receipt leaves the order
                // still owing units, and the panel offering the balance is the
                // one that would be unmounted by collapsing here.
                void queryClient.invalidateQueries({
                  queryKey: ["po-shipments", po.id],
                });
              }}
            />

            {canViewAudit && (
              <AuditTrail resourceType="purchase_order" resourceId={po.id} />
            )}
          </div>
        )}

        {actionErr && <p style={{ ...S.err, marginTop: 8 }}>{actionErr}</p>}
      </div>
    );
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h2 style={{ ...S.h2, margin: 0 }}>
          📦 Purchase Orders{" "}
          {pending.length > 0 && (
            <span
              style={{
                fontSize: 13,
                background: "#7c3aed",
                color: "#fff",
                borderRadius: 10,
                padding: "1px 7px",
              }}
            >
              {pending.length} pending approval
            </span>
          )}
          <span
            style={{ ...S.muted, fontSize: 14, fontWeight: 400, marginLeft: 8 }}
          >
            ({total} total)
          </span>
        </h2>
        <button style={S.btnSm} onClick={() => void refetch()}>
          ↺ Refresh
        </button>
      </div>

      {pos.length === 0 ? (
        <EmptyState message="No purchase orders yet. Approve a recommendation to generate one." />
      ) : (
        <>
          {pending.length > 0 && (
            <div>
              <h3 style={{ ...S.h3, color: "#7c3aed" }}>
                Awaiting Manager Approval ({pending.length})
              </h3>
              {pending.map(renderPO)}
            </div>
          )}
          {others.length > 0 && (
            <div>
              {pending.length > 0 && (
                <h3 style={{ ...S.h3, color: "#888", marginTop: 16 }}>
                  All Other POs ({others.length})
                </h3>
              )}
              {others.map(renderPO)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
